// /Users/suresh/dev/peach/peach/app/javascript/utils/notification_helpers.js

// Private helper function for Service Worker notifications
function _serviceWorkerNotify(title, body, options) {
  if (Notification.permission === 'granted') {
    navigator.serviceWorker.getRegistration().then(function (reg) {
      if (!reg) {
        console.warn('[NotificationHelper] Service worker registration not found. Falling back to standard notification.');
        _standardNotification(title, body, options); // Fallback
        return;
      }
      // Ensure options.url is correctly passed in the data payload for the SW
      const swDataPayload = {
        dateOfArrival: Date.now(),
        primaryKey: (options.data && options.data.primaryKey) || 1, // If primaryKey is part of customSwData
        url: options.url, // Crucial for click handling in SW
        ...(options.data || {}) // Merges any customSwData
      };

      const swOptions = {
        body: body,
        icon: options.icon || '/images/icon-192.png',
        badge: options.badge || '/images/badge-128.png',
        sound: options.sound, // Sound is unreliable in SW, browser/OS dependent
        data: swDataPayload,
        requireInteraction: !!options.requireInteraction,
      };

      console.log('[NotificationHelper] Showing SW notification with title:', title, 'options:', swOptions);
      reg.showNotification(title, swOptions)
        .then(() => {
          console.log('[NotificationHelper] Service worker notification shown successfully for:', title);
        })
        .catch((err) => {
          console.error('[NotificationHelper] Service worker showNotification() error:', err);
          console.warn('[NotificationHelper] Falling back to standard notification due to service worker showNotification() error.');
          _standardNotification(title, body, options); // Fallback
        });

    }).catch(err => {
      console.error('[NotificationHelper] Error getting service worker registration:', err);
      _standardNotification(title, body, options); // Fallback on error
    });
  } else {
    console.warn('[NotificationHelper] Notification permission not granted. Cannot show service worker notification.');
  }
}

// Private helper function for standard browser notifications
function _standardNotification(title, body, options) {
  if ('Notification' in window) {
    const notificationOptions = {
      body: body,
      icon: options.icon || '/images/icon-192.png',
      badge: options.badge || '/images/badge-128.png',
      requireInteraction: !!options.requireInteraction,
      ...(options.customStdOpts || {})
    };

    if (Notification.permission === 'granted') {
      try {
        console.log('[NotificationHelper] Showing standard notification with title:', title, 'options:', notificationOptions, 'sound:', options.sound, 'url:', options.url);
        const notification = new Notification(title, notificationOptions);

        if (options.sound) {
          console.log('[NotificationHelper] Attempting to play sound:', options.sound);
          const audio = new Audio(options.sound);
          audio.play()
            .then(() => console.log('[NotificationHelper] Sound played successfully:', options.sound))
            .catch(e => console.warn('[NotificationHelper] Standard Notification sound playback failed:', e));
        }

        notification.onclick = () => {
          console.log('[NotificationHelper] Standard notification clicked. URL:', options.url);
          if (options.url) {
            window.location.href = options.url;
          }
          if (notification.close) notification.close();
        };
        console.log('[NotificationHelper] Standard notification onclick handler set.');

      } catch (e) {
        console.error('[NotificationHelper] Error creating standard notification:', e);
      }
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then(function (permission) {
        if (permission === 'granted') {
          try {
            const notification = new Notification(title, notificationOptions);
            if (options.sound) { // Play sound for standard notification
              const audio = new Audio(options.sound);
              audio.play().catch(e => console.warn('[NotificationHelper] Standard Notification sound playback failed (after permission grant):', e));
            }
            notification.onclick = () => {
              if (options.url) {
                window.location.href = options.url;
              }
              if (notification.close) notification.close();
            };
          } catch (e) {
            console.error('[NotificationHelper] Error creating standard notification after permission grant:', e);
          }
        }
      });
    }
  } else {
    console.log('[NotificationHelper] This browser does not support desktop notification.');
  }
}

/**
 * Shows a browser notification.
 * @param {object} params - The notification parameters.
 * @param {string} params.title - The title of the notification.
 * @param {string} params.body - The body text of the notification.
 * @param {string} [params.url] - The URL to open when the notification is clicked.
 * @param {boolean} [params.requireInteraction=false] - Whether the notification requires user interaction to dismiss.
 * @param {string} [params.sound] - The sound to play.
 * @param {string} [params.type='message'] - The type of notification (e.g., 'message', 'incoming_call').
 * @param {string} [params.icon] - Custom icon path.
 * @param {string} [params.badge] - Custom badge path.
 * @param {object} [params.customSwData] - Custom data for the service worker notification.
 * @param {object} [params.customStdOpts] - Custom options for standard Notification API.
 */
export function showBrowserNotification({
  title,
  body,
  url,
  requireInteraction = false,
  sound,
  type = 'message', // Helps determine default behaviors
  icon,
  badge,
  customSwData,
  customStdOpts
}) {
  let effectiveRequireInteraction = requireInteraction;
  let effectiveSound = sound;

  if (type === 'incoming_call') {
    effectiveRequireInteraction = true; // Calls usually require interaction
    effectiveSound = sound !== undefined ? sound : '/call-tone.mp3';
  } else if (type === 'message' || type === 'wa_inbox_notification') {
    effectiveSound = sound !== undefined ? sound : '/notification-beep.mp3';
  }

  const commonOptions = {
    url: url,
    requireInteraction: effectiveRequireInteraction,
    sound: effectiveSound, // This sound is for the SW's notification option (unreliable)
    icon: icon,
    badge: badge,
    data: customSwData,
    customStdOpts: customStdOpts
  };

  console.log('[NotificationHelper] showBrowserNotification called. Type:', type, 'Calculated effectiveSound:', effectiveSound, 'URL:', url);

  if ('serviceWorker' in navigator && Notification.permission === 'granted') {
    navigator.serviceWorker.ready.then(registration => {
      console.log('[NotificationHelper] Service worker ready:', registration);
      if (registration && registration.active) {
        _serviceWorkerNotify(title, body, commonOptions); // Ask SW to show notification

        // Play sound from page context as SW sound is unreliable
        if (commonOptions.sound) {
          console.log('[NotificationHelper] Attempting to play sound from page context for SW notification:', commonOptions.sound);
          const audio = new Audio(commonOptions.sound);
          audio.play()
            .then(() => console.log('[NotificationHelper] Sound played successfully from page context for SW notification:', commonOptions.sound))
            .catch(e => console.warn('[NotificationHelper] Page context sound playback failed for SW notification:', e));
        }
      } else {
        console.warn('[NotificationHelper] Service worker not ready or not active, falling back to standard notification.');
        _standardNotification(title, body, commonOptions);
      }
    }).catch(error => {
      console.error('[NotificationHelper] Service worker readiness error:', error);
      _standardNotification(title, body, commonOptions); // Fallback
    });
  } else {
    _standardNotification(title, body, commonOptions);
  }
}
